// ==========================================================
// Time (String format)  Validations
// ==========================================================
function parseTimeString(timeStr) {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return new Date(1970, 0, 1, hours, minutes);
}

// Validation that check if End  is after fromTime, if not then throw error
export function toTimeAfterFromTime(fromKey = 'fromTime') {
  return function (toTime) {
    const fromTime = this.parent?.[fromKey];
    if (!fromTime || !toTime) return true;

    const from = parseTimeString(fromTime);
    const to = parseTimeString(toTime);

    return to > from;
  };
}

// Validation that check if Start time is before toTime, if not then throw error
export function fromTimeBeforeToTime(toKey = 'toTime') {
  return function (fromTime) {
    const toTime = this.parent?.[toKey];
    if (!fromTime || !toTime) return true;

    const from = parseTimeString(fromTime);
    const to = parseTimeString(toTime);

    return from < to;
  };
}

// Validation that check if time is more that hrs, if not then throw error
// Hrs =  3rs. So if time difference is less than 3rs then throw error
export function timeDiffLessThanOrEqualInHrs(hrs, fromKey = 'fromTime') {
  return function (toTime) {
    const fromTime = this.parent?.[fromKey];
    if (!fromTime || !toTime) return true;

    const from = parseTimeString(fromTime);
    const to = parseTimeString(toTime);

    let diff = (to - from) / (1000 * 60); // in minutes
    const maxDiff = hrs * 60;
    if (diff <= 0) diff += 1440; // handle next-day rollover

    return diff <= maxDiff;
  };
}

// Validation that check if time is less that hrs, if not then throw error
// Hrs = 24hrs. So if time difference is more than 24rs then throw error
export function timeDiffMoreThanEqualInHrs(hrs, fromKey = 'fromTime') {
  return function (toTime) {
    const fromTime = this.parent?.[fromKey];
    if (!fromTime || !toTime) return true;

    const from = parseTimeString(fromTime);
    const to = parseTimeString(toTime);

    let diff = (to - from) / (1000 * 60); // in minutes
    const minDiff = hrs * 60;
    if (diff <= 0) diff += 1440;

    return diff >= minDiff;
  };
}

// ==========================================================
// Date (String format) Validations
// ==========================================================

// Validation that check if End date is after from date, if not then throw error
export function endDateAfterStartDate(startKey = 'startDate') {
  return function (endDate) {
    const startDate = this.parent?.[startKey];
    if (!startDate || !endDate) return true;

    const start = new Date(startDate);
    const end = new Date(endDate);

    return end > start;
  };
}

// Test if to Date is same  as from date
export function endDateSameAsStartDate(fromKey = 'fromDate') {
  return function (toDate) {
    const fromDate = this.parent?.[fromKey];
    if (!fromDate || !toDate) return true;

    const from = new Date(fromDate);
    const to = new Date(toDate);

    return from.getTime() === to.getTime();
  };
}
// ==========================================================
// File Validation
// ==========================================================

// Validation that check if file size is less than maxSize in bytes
// 1MB = 1024 * 1024 bytes
export function validateFileSize(maxSize) {
  return function (file) {
    return file && file.size <= maxSize;
  };
}

// Validation that check if file format is in supportedFormats
export function validateFileFormat(supportedFormats) {
  return function (file) {
    return file && supportedFormats.includes(file.type);
  };
}

// Validation that check if multiple files size is less than maxSize in bytes
export function validateMultipleFilesSize(maxSize) {
  return function (files) {
    if (!files || files.length === 0) return true;
    return files.every(file => file.size <= maxSize);
  };
}

export function validateMultipleFilesFormat(supportedFormats) {
  const allowedTypes = supportedFormats.map(format => format?.type);
  const allowedExtensions = supportedFormats.map(format => format?.extension);
  return function (files) {
    if (allowedTypes.length === 0 && allowedExtensions.length === 0) {
      return true;
    }
    if (!files) return true;
    // Check if files is just an instance of a file that make it into an arrary
    let filesArray = files;
    if (files instanceof File) {
      filesArray = [files];
    }
    if (!filesArray || filesArray.length === 0) return true;
    return filesArray.every(file => {
      if (file?.type) {
        return allowedTypes.includes(file.type);
      }
      const ext = file?.name?.split('.')?.pop().toLowerCase();
      if (ext) {
        return allowedExtensions.includes(ext);
      }
      return false;
    });
  };
}

export const validateMultipleFilesFormatMessage = supportedFormats => {
  const formats = supportedFormats
    .map(format => format?.extension || format?.type)
    .join(', ');
  return `Unsupported file format. Supported formats: ${formats}`;
};

// 1. The whitelist regex for a “safe” filename:
//    - letters A–Z a–z
//    - digits 0–9
//    - spaces
//    - underscore (_), hyphen (-), dot (.)
const SAFE_FILENAME_REGEX = /^[0-9A-Za-z _\-.]+$/;

// 2. Validation function: takes a File (or falsy) and returns true if its name is safe
export function validateFileName(file) {
  if (!file) return true; // no file → valid (or you can choose to return false)
  return SAFE_FILENAME_REGEX.test(file.name);
}

// 3. Message builder in the same style as your others:
export const validateFileNameMessage = () =>
  'Unsupported file name. Only letters, numbers, spaces, underscores (_), hyphens (-), and dots (.) are allowed.';

// ==========================================================
// Number Validation
// ==========================================================
// Validation that checks a number is not less than zero
export function numberNotLessThanZero() {
  return function (value) {
    // allow empty/null (handled by required() elsewhere if needed)
    if (value == null) return true;
    return value >= 0;
  };
}

// Test to make sure is not equal to a speicifc number
export function numberNotEqualTo(value) {
  return function (testValue) {
    // allow empty/null (handled by required() elsewhere if needed)
    if (testValue == null) return true;
    return testValue !== value;
  };
}
